import { useId } from 'react'
import type { ReactNode } from 'react'
import { IconWrench } from '../../../components/icons'
import { Button } from '../../../components/ui/Button'
import type { ButtonVariant } from '../../../components/ui/Button'

export interface BulkActionBarProps {
  readonly selectedCount: number
  /** "Add to RFQ bundle" — BOM detail's bulk bar only. Now OPTIONAL (UID-257):
   * the design refresh's Library selection bar carries exactly Configure /
   * Request quote / Add to cart, so the Library no longer renders this button
   * (its RFQ path folded into "Request quote for N"). The button renders only
   * when this handler is supplied, so the Library omits it while BOM detail
   * keeps it unchanged. */
  readonly onAddToRfq?: () => void
  /** Optional — the bar is shared with the Parts Library, which has no
   * bulk cart/remove actions. Each button renders only when its handler
   * is supplied, so a consumer never gets a dead button. */
  readonly onAddToCart?: (() => void) | undefined
  /** Overrides the "Add to cart" label — the Parts Library listing (UID-170/F2,
   * screens.md §5 session-header bulk bar) shows "Add {n} to cart" with the live
   * selected count; BOM detail's bulk bar keeps the plain default label
   * unchanged (existing behavior/tests) by simply not passing this. */
  readonly cartLabel?: string
  /** UID — the "Add to cart" button's emphasis. The design gives it two
   * different treatments per surface: the Library selection bar renders it as
   * the solid brand-filled primary (`Fictiv Platform.dc.html:1345`), while BOM
   * detail renders it as a white-outline `secondary` (`:1941`). Defaults to
   * `'secondary'` so BOM detail (and any older caller) is unchanged; the
   * Library passes `'primary'`. */
  readonly cartVariant?: ButtonVariant
  /** UID-257 — the Library bar's "Add N to cart" is enabled only when EVERY
   * selected part is priced (design handoff §6/§8). When `true` the button
   * stays FOCUSABLE (`aria-disabled`, not native `disabled` — UID-285) and
   * renders greyed, with `addToCartDisabledReason` exposed to assistive tech
   * via `aria-describedby` (see the reason `<p>` this renders alongside the
   * button). Independent of the per-action `cartBusy` in-flight state. */
  readonly addToCartDisabled?: boolean
  readonly addToCartDisabledReason?: string
  readonly onRemove?: () => void
  /** UID-170/F2 — bulk "Configure" / "Request quote" (screens.md §5's
   * session-header bulk bar). Each reuses the SAME single-row destinations
   * the primary-action cell does (the C3 edit-configuration modal / the A3
   * RFQ affordance) — the caller is expected to no-op safely (not fake a
   * batch flow) when the selection doesn't resolve to exactly one row, since
   * neither destination has a real multi-row apply yet. */
  readonly onConfigure?: (() => void) | undefined
  readonly onRequestQuote?: (() => void) | undefined
  /** UID-418 — a caller-owned control rendered as the FIRST action in the bar.
   * The Library passes its `AddToProjectMenu` (a button-anchored overlay per
   * the design), keeping the anchored-popover logic out of this shared bar;
   * BOM detail passes nothing, so nothing renders there. */
  readonly addToProjectControl?: ReactNode
  /** UID-257 — count-bearing labels for the Library bar ("Configure 3",
   * "Request quote for 3"). Absent → the plain default labels ("Configure",
   * "Request quote"), keeping BOM detail / older callers unchanged. */
  readonly configureLabel?: string
  readonly requestQuoteLabel?: string
  /** UID-257 — the Library bar's "Request quote for N" is disabled/greyed
   * while any selected part still needs configuration (design handoff §8).
   * Same `aria-disabled`/`aria-describedby` treatment as `addToCartDisabled`
   * above (UID-285) — see that prop's doc comment. Independent of the
   * per-action `requestQuoteBusy` in-flight state. */
  readonly requestQuoteDisabled?: boolean
  readonly requestQuoteDisabledReason?: string
  readonly onClear: () => void
  /** Per-action busy flags (code-review UID-47 L1) — a single shared `busy`
   * previously made the "Add to RFQ bundle" button spin while a totally
   * different action (bulk remove/add-to-cart) was in flight. Each button
   * now only shows its OWN spinner/disabled state; the other two actions +
   * Clear stay disabled while any one action is running, so the user can't
   * fire a second mutation mid-flight. */
  readonly rfqBusy?: boolean
  readonly cartBusy?: boolean
  readonly removeBusy?: boolean
  readonly configureBusy?: boolean
  readonly requestQuoteBusy?: boolean
}

/**
 * Fixed-bottom multi-select action bar (components.md "Multi-select action
 * bar" — "Fixed-bottom bar: 'N selected' + contextual bulk actions (Add to
 * RFQ bundle / Add to cart / Remove selected / Clear)", "appears only when
 * ≥1 row selected"). A labelled `<section>` landmark + a live count so
 * assistive tech announces the selection change without the user having to
 * re-discover the checkboxes. "Remove selected" is folded in here (rather
 * than living as a standalone button beside the tree) so every bulk action
 * lives in one fixed-position surface, per the same components.md line.
 */
export function BulkActionBar({
  selectedCount,
  onAddToRfq,
  onAddToCart,
  cartLabel = 'Add to cart',
  cartVariant = 'secondary',
  addToCartDisabled = false,
  addToCartDisabledReason,
  onRemove,
  onConfigure,
  onRequestQuote,
  addToProjectControl,
  configureLabel = 'Configure',
  requestQuoteLabel = 'Request quote',
  requestQuoteDisabled = false,
  requestQuoteDisabledReason,
  onClear,
  rfqBusy = false,
  cartBusy = false,
  removeBusy = false,
  configureBusy = false,
  requestQuoteBusy = false,
}: BulkActionBarProps) {
  const requestQuoteReasonId = useId()
  const addToCartReasonId = useId()

  if (selectedCount === 0) return null

  const anyBusy = rfqBusy || cartBusy || removeBusy || configureBusy || requestQuoteBusy

  return (
    <section className="bulk-action-bar" aria-label="Bulk actions">
      {/* Design `Fictiv Platform.dc.html:1317-1319` — a filled accent count
          PILL + a separate "selected" label + a vertical divider before the
          actions. The visible pill/label are two `aria-hidden` spans; a single
          visually-hidden live region carries the whole "N selected" phrase, so
          assistive tech announces it as one string (not "N" then "selected",
          and never doubled) on each selection change. */}
      <div className="bulk-action-bar-count">
        <span className="sr-only" aria-live="polite">
          {selectedCount} selected
        </span>
        <span className="bulk-action-bar-count-badge" aria-hidden="true">
          {selectedCount}
        </span>
        <span className="bulk-action-bar-count-label" aria-hidden="true">
          selected
        </span>
      </div>
      <span className="bulk-action-bar-divider" aria-hidden="true" />
      <div className="bulk-action-bar-actions">
        {/* UID-418 — the caller's "Add to project" control (an anchored overlay,
            per design) leads the Library selection bar's actions. */}
        {addToProjectControl}
        {onConfigure && (
          // Design `Fictiv Platform.dc.html:1343` — a solid warning-orange
          // button (#F0811E === `--color-warning`, via `.btn-warning`) with a
          // leading wrench glyph, matching the per-row "Configure" CTA and the
          // row's own needs-config urgency; not the flat grey outline this
          // previously used. `IconWrench` is the design's own verbatim glyph
          // (icons.tsx), decorative alongside the text label.
          <Button
            variant="warning"
            size="sm"
            className="bulk-action-bar-btn"
            onClick={onConfigure}
            loading={configureBusy}
            disabled={anyBusy && !configureBusy}
          >
            <IconWrench />
            {configureLabel}
          </Button>
        )}
        {onRequestQuote && (
          // UID-225: outlined purple (`.btn-outline-purple`), matching the
          // design's "Request quote for N" bulk-bar CTA (`Fictiv
          // Platform.dc.html` ~L60) — not the generic grey `.btn-outline`
          // the build previously used.
          // UID-257/UID-285: gated — greyed while any selected part still
          // needs configuration (design handoff §8). Stays natively
          // `disabled` ONLY for the transient busy-lockout (another bulk
          // action mid-flight) — the gating itself uses `aria-disabled` so
          // the button stays focusable/reachable by keyboard, with the
          // reason exposed via `aria-describedby` (a native `disabled`
          // button is pulled out of the tab order and its `title` is never
          // announced by a screen reader, so neither conveys WHY to
          // assistive-tech users — UID-285).
          <Button
            variant="outline-purple"
            size="sm"
            className="bulk-action-bar-btn"
            onClick={() => {
              if (requestQuoteDisabled) return
              onRequestQuote()
            }}
            loading={requestQuoteBusy}
            disabled={anyBusy && !requestQuoteBusy}
            aria-disabled={requestQuoteDisabled || undefined}
            aria-describedby={requestQuoteDisabled ? requestQuoteReasonId : undefined}
            title={requestQuoteDisabled ? requestQuoteDisabledReason : undefined}
          >
            {requestQuoteLabel}
          </Button>
        )}
        {/* UID-225: outlined purple, matching the "Request quote"/"Add to
            RFQ" bulk-bar treatment the fix list calls for — NOT the solid
            `.btn-purple` fill this shared bar previously used (the mock's
            OWN solid-purple button lives on a separate always-visible "RFQ
            bundle" summary banner, not this per-selection bulk bar; see the
            fix-list item's own framing that both bulk-bar CTAs are
            outlined). */}
        {onAddToRfq && (
          <Button
            variant="outline-purple"
            size="sm"
            className="bulk-action-bar-btn"
            onClick={onAddToRfq}
            loading={rfqBusy}
            disabled={anyBusy && !rfqBusy}
          >
            Add to RFQ bundle
          </Button>
        )}
        {onAddToCart && (
          // UID-257/UID-285: gated — enabled only when every selected part
          // is priced (design handoff §6/§8); same focusable `aria-disabled`
          // + `aria-describedby` treatment as "Request quote for N" above.
          <Button
            variant={cartVariant}
            size="sm"
            className="bulk-action-bar-btn"
            onClick={() => {
              if (addToCartDisabled) return
              onAddToCart()
            }}
            loading={cartBusy}
            disabled={anyBusy && !cartBusy}
            aria-disabled={addToCartDisabled || undefined}
            aria-describedby={addToCartDisabled ? addToCartReasonId : undefined}
            title={addToCartDisabled ? addToCartDisabledReason : undefined}
          >
            {cartLabel}
          </Button>
        )}
        {onRemove && (
          <Button
            variant="danger"
            size="sm"
            className="bulk-action-bar-btn"
            onClick={onRemove}
            loading={removeBusy}
            disabled={anyBusy && !removeBusy}
          >
            Remove selected
          </Button>
        )}
        <Button
          variant="link"
          size="sm"
          className="bulk-action-bar-btn"
          onClick={onClear}
          disabled={anyBusy}
        >
          Clear
        </Button>
      </div>
      {/* UID-285 — visually-hidden reason text an `aria-describedby` points
          at, so a screen-reader user landing on the disabled-by-gating
          button hears WHY, the same information a sighted user gets from
          the `title` tooltip. Rendered only while its button is actually
          gated-disabled (mirrors `CheckoutSummaryRail`'s
          `termsCaptionId`/`checkout-place-order-btn` pattern). */}
      {requestQuoteDisabled && requestQuoteDisabledReason && (
        <p id={requestQuoteReasonId} className="sr-only">
          {requestQuoteDisabledReason}
        </p>
      )}
      {addToCartDisabled && addToCartDisabledReason && (
        <p id={addToCartReasonId} className="sr-only">
          {addToCartDisabledReason}
        </p>
      )}
    </section>
  )
}
